import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import './DistributionBar.scss';

/**
 * DistributionBar - A segmented progress bar showing KC mastery distribution
 * Displays mastered, in progress, and not started percentages
 */
const DistributionBar = ({ 
  masteredPercentage = 0, 
  inProgressPercentage = 0, 
  notStartedPercentage = 0,
  showPercentagesAbove = false,
  showLegend = false,
  className = ''
}) => {
  const { t } = useTranslation(['chatbot']);

  const segments = [
    {
      key: 'mastered',
      percentage: masteredPercentage,
      label: t('kc_mastered_full', { defaultValue: 'Contenidos dominados' }),
      className: 'segment--mastered',
      color: '#0095A2'
    },
    {
      key: 'in-progress',
      percentage: inProgressPercentage,
      label: t('kc_in_progress'),
      className: 'segment--in-progress',
      color: '#00B4C5'
    },
    {
      key: 'not-started',
      percentage: notStartedPercentage,
      label: t('kc_not_started_full', { defaultValue: 'Contenidos sin empezar' }),
      className: 'segment--not-started',
      color: '#D4FBFF'
    }
  ];

  // Calculate cumulative positions for percentage labels
  // Apply minimum width to very small segments
  const MIN_SEGMENT_WIDTH = 5; // 5% minimum width
  const adjustedSegments = segments.map(segment => ({
    ...segment,
    displayPercentage: Math.max(segment.percentage, segment.percentage > 0 ? MIN_SEGMENT_WIDTH : 0)
  }));
  
  // Normalize to ensure total is 100%
  const totalDisplay = adjustedSegments.reduce((sum, seg) => sum + seg.displayPercentage, 0);
  if (totalDisplay > 100) {
    const scale = 100 / totalDisplay;
    adjustedSegments.forEach(seg => {
      seg.displayPercentage *= scale;
    });
  }
  
  const cumulativePositions = [];
  let cumulative = 0;
  adjustedSegments.forEach((segment, index) => {
    const position = {
      ...segment,
      start: cumulative,
      end: cumulative + segment.displayPercentage,
      originalPercentage: segments[index].percentage
    };
    cumulativePositions.push(position);
    cumulative += segment.displayPercentage;
  });

  return (
    <div className={`distribution-bar ${className}`}>
      <div 
        className="distribution-bar__container"
        role="progressbar"
        aria-label={t('analytics_kc_distribution')}
        aria-valuenow={masteredPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div className="distribution-bar__segments-wrapper">
          {cumulativePositions.map((position, index) => (
          position.originalPercentage > 0 && (
            <div
              key={position.key}
              className="distribution-bar__segment-wrapper distribution-bar__segment-wrapper--with-border"
              style={{ 
                flex: position.displayPercentage,
                minWidth: position.displayPercentage > 0 ? '40px' : '0'
              }}
            >
              {showPercentagesAbove && (
                <div className="distribution-bar__label-container">
                  <span className="distribution-bar__percentage-value">
                    {position.originalPercentage.toFixed(0)}%
                  </span>
                </div>
              )}
              
              <div
                className={`distribution-bar__segment ${position.className}`}
                style={{ backgroundColor: position.color }}
                title={`${position.label}: ${position.originalPercentage.toFixed(1)}%`}
              />
            </div>
          )
        ))}
        </div>
      </div>

      {showLegend && (
        <div className="distribution-bar__legend">
          {segments.map(segment => (
            <div key={segment.key} className="distribution-bar__legend-item">
              <span 
                className="distribution-bar__legend-color" 
                style={{ backgroundColor: segment.color }}
              />
              <span className="distribution-bar__legend-text">
                {segment.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

DistributionBar.propTypes = {
  masteredPercentage: PropTypes.number,
  inProgressPercentage: PropTypes.number,
  notStartedPercentage: PropTypes.number,
  showPercentagesAbove: PropTypes.bool,
  showLegend: PropTypes.bool,
  className: PropTypes.string
};

export default DistributionBar;